import { useTranslation } from 'react-i18next';
import SCUIGenericExcelUpload from '../../../../atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import { SCUICustomDataGrid } from '../../../../atomic/molecules/SCUICustomDataGrid/SCUICustomDataGrid';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Chip, Paper } from '@mui/material';
import Swal from 'sweetalert2';
import { useState } from 'react';
import ShippingCommand from '../../../../services/command/shipping/ShippingCommand.services';
import { MPCFM } from '../../mpCargoFeeManagement.classes';

export const BulkExcelUploadTab = ({
    setIsLoading,
    isLoading,
    companyOptions,
}: {
    setIsLoading: (isLoading: boolean) => void;
    isLoading: boolean;
    companyOptions: any[];
}) => {
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [uploadResponse, setUploadResponse] = useState<any>(null);

    const { t } = useTranslation();

    const renderHighlightedToken = (text: string, token: string) => {
        const parts = text.split(token);
        if (parts.length <= 1) return text;

        return (
            <>
                {parts.map((part, idx) => (
                    <span style={{ fontSize: '12px' }} key={idx}>
                        {idx > 0 && (
                            <span
                                style={{
                                    fontWeight: 700,
                                    backgroundColor: 'var(--rules-token-bg)',
                                    border: '1px solid var(--rules-token-border)',
                                    borderRadius: '8px',
                                    padding: '1px 6px',
                                    color: 'var(--rules-token-color)',
                                }}
                            >
                                {token}
                            </span>
                        )}
                        {part}
                    </span>
                ))}
            </>
        );
    };

    const exampleData = [
        {
            desi: 0,
            cost: 15.5,
            startDate: '01.01.2026',
            endDate: '30.01.2026',
        },
        {
            desi: 1,
            cost: 19.75,
            startDate: '01.01.2026',
            endDate: '31.01.2026',
        },
    ];

    const onUpload = async (file: File) => {
        setIsLoading(true);
        try {
            setUploadResponse(null);
            const formData = new FormData();
            formData.append('excel', file, file.name);
            formData.append('shippingCompanyId', selectedCompanyId);
            const response = await ShippingCommand.InsertBulkSellerShipmentCostsExcel(formData);
            if (response) {
                setUploadResponse(response);

                if (response.isSuccess === false) {
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: t('general.anErrorOccured'),
                        confirmButtonText: t('general.ok'),
                    });
                    return;
                }

                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        } catch {
            setUploadResponse(null);

            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.anErrorOccured'),
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeCompany = (event: SelectChangeEvent) => {
        setSelectedCompanyId(event.target.value);
    };

    // Render status counts
    const renderStatusCounts = () => {
        if (!uploadResponse) return null;

        const messageText = String(uploadResponse.message || '');
        const insertedRecords = Number(messageText.match(/Yeni:\s*(\d+)/)?.[1] ?? 0);
        const updatedRecords = Number(messageText.match(/Güncellenen:\s*(\d+)/)?.[1] ?? 0);

        const failedRecords = Array.isArray(uploadResponse?.errors)
            ? uploadResponse.errors.length
            : Object.keys(uploadResponse?.errors || {}).length;

        return (
            <Box className={MPCFM.bulkTabStatusRow} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, marginBottom: 2 }}>
                <Chip
                    label={`${t('mpCargoFeeManagement.updated')}: ${updatedRecords}`}
                    sx={{ backgroundColor: '#ff9800', color: 'white' }}
                />
                <Chip
                    label={`${t('mpCargoFeeManagement.inserted')}: ${insertedRecords}`}
                    sx={{ backgroundColor: '#4caf50', color: 'white' }}
                />
                {failedRecords > 0 && (
                    <Chip
                        label={`${t('mpCargoFeeManagement.failed')}: ${failedRecords}`}
                        sx={{ backgroundColor: '#f44336', color: 'white' }}
                    />
                )}
            </Box>
        );
    };

    // Render failed items table
    const renderFailedItemsTable = () => {
        const hasLegacyFailedItems =
            Array.isArray(uploadResponse?.failedItems) && uploadResponse.failedItems.length > 0;
        const hasErrorsArray = Array.isArray(uploadResponse?.errors) && uploadResponse.errors.length > 0;
        const hasErrorsObject =
            !!uploadResponse?.errors &&
            !Array.isArray(uploadResponse.errors) &&
            Object.keys(uploadResponse.errors || {}).length > 0;

        if (!hasLegacyFailedItems && !hasErrorsArray && !hasErrorsObject) return null;

        // Keep the same grid UI/columns, but support the new API model:
        // { errors: string[], isSuccess: boolean, message: string, error: any }
        const failedRows = hasErrorsObject
            ? Object.entries(uploadResponse.errors || {}).map(([rowNumber, message]) => ({
                  rowNumber,
                  message,
                  uniqueKey: rowNumber,
              }))
            : hasErrorsArray
            ? uploadResponse.errors.map((message: string, index: number) => ({
                  rowNumber: index + 1,
                  columnName: '-',
                  message,
                  uniqueKey: `err-${index}`,
              }))
            : uploadResponse.failedItems.flatMap((item: any) =>
                  item.errorModelList.map((error: any, errorIndex: number) => ({
                      rowNumber: item.index,
                      columnName: error.fieldName,
                      message: error.message,
                      uniqueKey: `${item.index}-${errorIndex}`,
                  }))
              );

        return (
            <Box className={MPCFM.bulkTabFailedSection} sx={{ marginTop: 3 }}>
                <p
                    className={MPCFM.bulkTabFailedTitle}
                    style={{ marginBottom: 2, color: '#f44336', fontWeight: 'bold', fontSize: '16px' }}
                >
                    {t('mpCargoFeeManagement.failedRecords')}
                </p>
                <Box className={MPCFM.bulkTabFailedGridWrap}>
                    <SCUICustomDataGrid
                        rows={failedRows}
                        getRowId={(row: any) => row.uniqueKey}
                        page={0}
                        pageSize={5}
                        columns={[
                            {
                                field: 'rowNumber',
                                headerName: t('mpCargoFeeManagement.rowNumber'),
                                flex: 0.5,
                            },
                            {
                                field: 'message',
                                headerName: t('mpCargoFeeManagement.message'),
                                flex: 2.5,
                            },
                        ]}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box className={MPCFM.bulkTabRoot} width="100%">
            <p className={MPCFM.bulkTabTitle} style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {t('fastDeliveryCargoManagement.bulkUpload')}
            </p>
            <p className={MPCFM.bulkTabSubtitle} style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('mpCargoFeeManagement.bulkUploadText')}
            </p>

            <FormControl
                className={MPCFM.bulkTabCompanyField}
                fullWidth
                sx={{ width: '40%', marginBottom: '16px', marginTop: '16px' }}
            >
                <InputLabel id="filter-company-label">{t('mpCargoFeeManagement.list.columns.cargoCompany')}</InputLabel>
                <Select
                    className={MPCFM.inputSelectBulkTabCompany}
                    labelId="filter-company-label"
                    label={t('mpCargoFeeManagement.list.columns.cargoCompany')}
                    value={selectedCompanyId}
                    onChange={handleChangeCompany}
                >
                    {(companyOptions || []).map(
                        (opt) =>
                            opt.id !== -1 && (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                </MenuItem>
                            )
                    )}
                </Select>
            </FormControl>
            <Paper
                className={MPCFM.bulkTabRulesPaper}
                sx={(theme) => ({
                    '--rules-title-color': '#1a1a1a',
                    '--rules-text-color': '#4a4a4a',
                    '--rules-token-bg': 'rgba(0,0,0,0.08)',
                    '--rules-token-border': 'rgba(0,0,0,0.15)',
                    '--rules-token-color': '#1a1a1a',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid rgba(0,0,0,0.12)',
                    ...theme.applyStyles('dark', {
                        '--rules-title-color': '#ffffff',
                        '--rules-text-color': '#b0b0b0',
                        '--rules-token-bg': 'rgba(255,255,255,0.1)',
                        '--rules-token-border': 'rgba(255,255,255,0.2)',
                        '--rules-token-color': '#e0e0e0',
                        backgroundColor: '#1e1e1e',
                        border: '1px solid rgba(255,255,255,0.12)',
                    }),
                    borderRadius: '8px',
                    padding: '14px 18px',
                    marginTop: '16px',
                    marginBottom: '16px',
                    boxShadow: 'none',
                })}
            >
                <p
                    style={{
                        margin: 0,
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'var(--rules-title-color)',
                    }}
                >
                    {t('mpCargoFeeManagement.excelRules.title')}
                </p>
                <ul
                    style={{
                        margin: '10px 0 0 0',
                        paddingLeft: '18px',
                        fontSize: '12px',
                        lineHeight: '2',
                        color: 'var(--rules-text-color)',
                    }}
                >
                    <li>{renderHighlightedToken(t('mpCargoFeeManagement.excelRules.dateFormat'), 'dd.MM.yyyy')}</li>
                    <li>
                        {renderHighlightedToken(
                            t('mpCargoFeeManagement.excelRules.desiCannotBeNegative'),
                            t('mpCargoFeeManagement.excelRules.negative')
                        )}
                    </li>
                    <li>{renderHighlightedToken(t('mpCargoFeeManagement.excelRules.costCannotBeNegative'), '2')}</li>
                    <li>{renderHighlightedToken(t('mpCargoFeeManagement.excelRules.decimalSeparator'), '1,5')}</li>
                    <li>{t('mpCargoFeeManagement.excelRules.startBeforeEnd')}</li>
                    <li>{t('mpCargoFeeManagement.excelRules.noOverlappingDateRanges')}</li>
                </ul>
            </Paper>
            <Box className={MPCFM.bulkTabUploadWrap}>
                <SCUIGenericExcelUpload
                    onUpload={onUpload}
                    uploadButtonText={t('fastDeliveryCargoManagement.upload')}
                    selectFileButtonText={t('fastDeliveryCargoManagement.selectExcelFile')}
                    showExample={true}
                    exampleData={exampleData}
                    exampleButtonText={t('general.exampleExcel')}
                    loading={isLoading}
                    style={{ width: '100%' }}
                    disabled={!selectedCompanyId}
                    exampleFileName={t('mpCargoFeeManagement.exampleFileName')}
                />
            </Box>

            {/* Display status counts */}
            {renderStatusCounts()}

            {/* Display failed items table */}
            {renderFailedItemsTable()}
        </Box>
    );
};

export default BulkExcelUploadTab;
